/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useState} from 'react'
import axios from 'axios'
import {API_URL} from '../../../modules/auth/core/_requests'
import { useAuth } from '../../../modules/auth'
import AIOptions, {AIOptionValues, defaultAIOptionValues} from './components/AIOptions'

export const InnerLinkerPage: FC = () => {
  const { currentUser } = useAuth()
  const currentStoreName = currentUser?.decoded?.current_shopify_store || ''

  const [url, setUrl] = useState('')
  const [editedHtml, setEditedHtml] = useState('')
  const [aiOptions, setAiOptions] = useState<AIOptionValues>(defaultAIOptionValues)
  const [insertedLinks, setInsertedLinks] = useState<{ keyword: string, url: string }[]>([])
  const [loading, setLoading] = useState(false)

  // Helper: Extract domain name from a URL string.
  const getDomainFromUrl = (inputUrl: string): string => {
    try {
      return new URL(inputUrl).hostname
    } catch (error) {
      return ""
    }
  }

  // Fetches the content for the Shopify page via our backend.
  const fetchContent = async () => {
    if (!url.trim()) {
      alert('Please enter a valid URL.')
      return
    }
    setLoading(true)
    try {
      const domain = getDomainFromUrl(url.trim())
      const resp = await axios.get(`${API_URL}/api/shopify/get`, {
        params: {
          storeName: currentStoreName,
          url: url.trim(),
          domain: domain,
        },
      })
      setEditedHtml(resp.data.content)
    } catch (err: any) {
      console.error(err)
      alert(err.response?.data?.error || 'Failed to fetch content from Shopify.')
    } finally {
      setLoading(false)
    }
  }

  // Adds inner links using the innerlink API endpoint.
  const handleAddInnerLinks = async () => {
    if (!editedHtml.trim()) {
      alert('No content available to process for internal linking.')
      return
    }
    setLoading(true)
    try {
      const domain = getDomainFromUrl(url.trim())
      const resp = await axios.post(`${API_URL}/api/auto_innerlink/insert`, {
        storeName: currentStoreName,
        blogContent: editedHtml.trim(), // reusing the same parameter name as in the auto-blogger
        domain: domain,
        aiModel: aiOptions.aiModel,
        aiEffort: aiOptions.aiEffort,
      })
      setEditedHtml(resp.data.modifiedHtml)
      setInsertedLinks(resp.data.insertedLinks || [])
      alert('Internal linking added successfully!')
    } catch (err: any) {
      console.error(err)
      alert(err.response?.data?.error || 'Failed to process internal linking.')
    } finally {
      setLoading(false)
    }
  }

  // Pushes the updated HTML content back to Shopify via our backend.
  const handlePushToShopify = async () => {
    if (!url.trim()) {
      alert('URL is required to push updates.')
      return
    }
    if (!editedHtml.trim()) {
      alert('There is no HTML content to push.')
      return
    }
    setLoading(true)
    try {
      const domain = getDomainFromUrl(url.trim())
      const resp = await axios.post(`${API_URL}/api/shopify/update`, {
        storeName: currentStoreName,
        url: url.trim(),
        content: editedHtml.trim(),
        domain: domain,
      })
      alert(resp.data.message || 'Page updated successfully on Shopify!')
      // Reset the form.
      setUrl('')
      setEditedHtml('')
      setInsertedLinks([])
    } catch (err: any) {
      console.error(err)
      alert(err.response?.data?.error || 'Failed to push updated HTML to Shopify.')
    } finally {
      setLoading(false)
    }
  }

  // Reset all form state
  const handleReset = () => {
    setUrl('')
    setEditedHtml('')
    setInsertedLinks([])
  }

  return (
    <>
      <h1 className="mt-3 ms-3">InnerLinker</h1>
      <div className="card mx-3 mt-4">
        <div className="card-body">
          {/* URL Input */}
          <div className="mb-3">
            <label className="form-label">
              Shopify Page URL <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Enter the Shopify page URL..."
            />
          </div>
          {/* AI Options */}
          <AIOptions value={aiOptions} onChange={setAiOptions} showPromptExtra={false} />
          {/* Action Buttons */}
          <div className="mb-3">
            <button
              className="btn btn-secondary me-3"
              onClick={fetchContent}
              disabled={loading}
            >
              Fetch Content
            </button>
            <button
              className="btn btn-warning me-3"
              onClick={handleAddInnerLinks}
              disabled={loading || !editedHtml.trim()}
            >
              Add Inner Links
            </button>
            <button
              className="btn btn-primary me-3"
              onClick={handlePushToShopify}
              disabled={loading || !editedHtml.trim()}
            >
              Push to Shopify
            </button>
            <button
              className="btn btn-link text-white"
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Display the inserted inner links (if any) */}
      {insertedLinks.length > 0 && (
        <div className="card mx-3 mt-2">
          <div className="card-header">Inserted Inner Links</div>
          <div className="card-body">
            <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
              {insertedLinks.map((link, idx) => (
                <li key={idx}>
                  <strong>{link.keyword}</strong> linked to <code>{link.url}</code>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Editor and Preview Panels */}
      <div className="row mx-3 mt-3">
        <div className="col-md-6 mb-3">
          <div className="card">
            <div className="card-header">Editable HTML</div>
            <div className="card-body" style={{ minHeight: '300px' }}>
              {loading ? (
                <div className="text-muted">Loading...</div>
              ) : (
                <textarea
                  className="form-control"
                  style={{ height: '300px' }}
                  value={editedHtml}
                  onChange={(e) => setEditedHtml(e.target.value)}
                />
              )}
            </div>
          </div>
        </div>
        <div className="col-md-6 mb-3">
          <div className="card">
            <div className="card-header">Rendered Preview</div>
            <div className="card-body" style={{ minHeight: '300px', overflowY: 'auto' }}>
              {loading ? (
                <div className="text-muted">Loading...</div>
              ) : editedHtml ? (
                <div dangerouslySetInnerHTML={{ __html: editedHtml }} />
              ) : (
                <p className="text-muted">No HTML content available.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export const InnerLinkerWrapper: FC = () => {
  return <InnerLinkerPage />
}
